import { useState, useRef, useEffect } from "react";

/**
 * SmartImage: enquadra fotos sem cortar pessoas/objetos importantes.
 *
 * Estratégia:
 * - Container com aspect-ratio fixo (ou modo "fill" pra preencher o pai).
 * - Quando a imagem carrega, detecta orientação:
 *   • Paisagem ou square → object-cover (preenche bem o card)
 *   • Retrato → object-contain + fundo borrado da própria imagem
 *
 * Props:
 *   aspect: "16/9" | "4/3" | "square" | "9/16" | "fill" (default 16/9)
 *   src, alt, rounded, className, imgClassName, overlay, onError
 */
export default function SmartImage({
  src,
  alt = "",
  aspect = "16/9",
  rounded = "rounded-xl",
  className = "",
  imgClassName = "",
  overlay = null,
  onError,
}) {
  const [ratio, setRatio] = useState(null);
  const imgRef = useRef(null);

  const aspectClass =
    aspect === "fill"
      ? "absolute inset-0 w-full h-full"
      : aspect === "square"
      ? "aspect-square"
      : aspect === "4/3"
      ? "aspect-[4/3]"
      : aspect === "9/16"
      ? "aspect-[9/16]"
      : "aspect-[16/9]";

  const containerRatio =
    aspect === "fill"
      ? 16 / 9
      : aspect === "square"
      ? 1
      : aspect === "4/3"
      ? 4 / 3
      : aspect === "9/16"
      ? 9 / 16
      : 16 / 9;

  const handleLoad = (e) => {
    const img = e.currentTarget;
    if (img.naturalWidth && img.naturalHeight) {
      setRatio(img.naturalWidth / img.naturalHeight);
    }
  };

  useEffect(() => {
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth && img.naturalHeight) {
      setRatio(img.naturalWidth / img.naturalHeight);
    }
  }, [src]);

  if (!src) {
    return (
      <div
        className={`${aspectClass} ${rounded} bg-zinc-900 ${className}`}
      />
    );
  }

  // Retrato em container paisagem? → contain + blur. Caso contrário cover.
  const isPortrait = ratio !== null && ratio < containerRatio * 0.95;
  const fitClass = isPortrait ? "object-contain" : "object-cover";

  return (
    <div
      className={`relative ${aspectClass} ${rounded} overflow-hidden bg-zinc-900 ${className}`}
    >
      {isPortrait && (
        <img
          src={src}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-50 pointer-events-none"
          loading="lazy"
        />
      )}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        onLoad={handleLoad}
        onError={onError}
        className={`relative w-full h-full ${fitClass} transition-transform duration-500 group-hover:scale-105 ${imgClassName}`}
      />
      {overlay}
    </div>
  );
}
